import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SystemSettingsServices from "../../services/SystemSettings";

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    id: null,
    storage_type: 'dropbox',
    dropbox_access_token: '',
    ftp_host: '',
    ftp_port: 21,
    ftp_username: '',
    ftp_password: '',
    ftp_base_path: '/',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await SystemSettingsServices.getActiveSystemSettings();
      if (data) {
        setSettings({
          ...data,
          ftp_port: data.ftp_port ? parseInt(data.ftp_port, 10) : 21
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Prepare data with proper types
      const dataToSave = {
        ...settings,
        ftp_port: settings.ftp_port ? parseInt(settings.ftp_port, 10) : 21
      };

      // Remove id from data to save
      const { id, ...saveData } = dataToSave;

      if (settings.id) {
        // Update existing settings
        await SystemSettingsServices.updateSystemSettings(settings.id, saveData);
      } else {
        // Create new settings
        await SystemSettingsServices.createSystemSettings(saveData);
      }

      setMessage('Settings saved successfully!');
      fetchSettings(); // Refresh the data
    } catch (error) {
      setMessage(`Failed to save settings: ${error.message || 'Unknown error'}`);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>System Settings</h4>
            <h6>Configure File Upload Storage</h6>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {message && (
              <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-danger'} mb-3`}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="input-blocks">
                    <label className="form-label">Storage Type</label>
                    <select
                      name="storage_type"
                      className="form-control"
                      value={settings.storage_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="dropbox">Dropbox</option>
                      <option value="ftp">FTP</option>
                    </select>
                  </div>
                </div>

                {settings.storage_type === 'dropbox' && (
                  <div className="col-lg-6 col-sm-12">
                    <div className="input-blocks">
                      <label className="form-label">Dropbox Access Token</label>
                      <input
                        type="password"
                        name="dropbox_access_token"
                        className="form-control"
                        value={settings.dropbox_access_token}
                        onChange={handleInputChange}
                        placeholder="Enter Dropbox access token"
                        required
                      />
                    </div>
                  </div>
                )}

                {settings.storage_type === 'ftp' && (
                  <>
                    <div className="col-lg-6 col-sm-12">
                      <div className="input-blocks">
                        <label className="form-label">FTP Host</label>
                        <input
                          type="text"
                          name="ftp_host"
                          className="form-control"
                          value={settings.ftp_host}
                          onChange={handleInputChange}
                          placeholder="ftp.example.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-sm-12">
                      <div className="input-blocks">
                        <label className="form-label">FTP Port</label>
                        <input
                          type="number"
                          name="ftp_port"
                          className="form-control"
                          value={settings.ftp_port}
                          onChange={handleInputChange}
                          min="1"
                          max="65535"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-sm-12">
                      <div className="input-blocks">
                        <label className="form-label">FTP Username</label>
                        <input
                          type="text"
                          name="ftp_username"
                          className="form-control"
                          value={settings.ftp_username}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-sm-12">
                      <div className="input-blocks">
                        <label className="form-label">FTP Password</label>
                        <input
                          type="password"
                          name="ftp_password"
                          className="form-control"
                          value={settings.ftp_password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-sm-12">
                      <div className="input-blocks">
                        <label className="form-label">FTP Base Path</label>
                        <input
                          type="text"
                          name="ftp_base_path"
                          className="form-control"
                          value={settings.ftp_base_path}
                          onChange={handleInputChange}
                          placeholder="/uploads or /documents"
                        />
                        <small className="text-muted">Leave empty or use "/" for root directory</small>
                      </div>
                    </div>
                  </>
                )}

                <div className="col-lg-6 col-sm-12">
                  <div className="input-blocks">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="form-check-input"
                        checked={settings.is_active}
                        onChange={handleInputChange}
                        id="isActive"
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active Settings
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-submit me-2"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                  <Link to="#" className="btn btn-cancel">
                    Cancel
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;